/**
 * Created by simon on 2018-11-14.
 */

import _     from 'lodash';
import axios from 'axios';
import Vue   from 'vue';
import {$t}  from '../plugins/i18n.js';

const APIURL = process.env.VUE_APP_API_URI;
const isDev  = process.env.VUE_APP_UTV === 'true';

const httpClient = axios
	.create({
		baseURL: APIURL,
		headers: {
			'DV-API-REQUEST': true,
		},
		withCredentials: isDev,
	});

httpClient.interceptors.response.use((response) => {
	if(response.headers && response.headers['x-build-id']) {
		Vue.prototype.$eventBus.$emit('WebSocketClient:UpdateBuildID', response.headers['x-build-id']);
	}

	return response;
});

import BaseModel from '../models/BaseModel.js';

import Store from '../store/index.js';

class BackendError extends Error {
	constructor(message, dvCode) {
		super(message);

		this.name   = 'BackendError';
		this.DVCode = dvCode;
	}
}

const handleError = (error, options = {}) => {
	if(Store.state.NetworkOffline === true) {
		Store.dispatch('setSnackbar', {
			text:  $t('InternetConnectionOffline'),
			color: 'error',
		});

		return;
	}

	const isAuthenticated = Store.state.Authenticated;
	const isSessionDestroyed = isAuthenticated &&
		(
			(_.get(error, 'response.status', null) === 403 && _.toString(_.get(error, 'response.data.dvCode', null)).slice(0, 3) !== '403') ||
			(_.get(error, 'response.status', null) === 401)
		);

	if(isSessionDestroyed) {
		const messageTemplate = _.get(error, 'response.data.messageTemplate', null);
		const message = messageTemplate
			? $t(messageTemplate)
			: $t('SessionEndedAbruptly')

		Store.dispatch('setSnackbar', {
			text:  message,
			color: 'error',
		});
		Store.dispatch('logout', {saveSnackbarState: true});

		return;
	}

	//Overwrite message with backend message if it exists
	const backendMessage = _.get(error, 'response.data.message', null);

	if(backendMessage) {
		if(!options.skipBackendMessageSnackbar) {
			Store.dispatch('setSnackbar', {
				text:  backendMessage,
				color: 'error',
			});
		}

		throw new BackendError(backendMessage, error.response.data.dvCode);
	}

	//Network error
	if((!error.response || error.message === 'Network Error') && error.config && _.last(error.config.url.split('/')) !== 'me') {
		Store.dispatch('setSnackbar', {
			text:  `Störningar har upptäckts i nätverkskommunikationen. Kontrollera din internetanslutning och försök igen.`,
			color: 'error',
		});

		return;
	}

	throw error;
};

class HttpRequester {
	static get BackendError() {
		return BackendError;
	}

	static async get(url, options = {}) {
		try {
			const getOptions = structuredClone(options);

			let isMobileApp = false;

			if(options.isMobileApp && options.responseType) {
				isMobileApp = true;
				getOptions.baseURL = options.baseURL;
				getOptions.responseType = options.responseType;
				delete options.isMobileApp;
				delete options.responseType;
				delete options.baseURL;
			}

			const getResult = await httpClient.get(url, appendSelectionHeaders(getOptions));

			const {data, headers = {}} = getResult;

			if(headers && headers['x-pagination-info']) {
				data.PaginationInfo = JSON.parse(headers['x-pagination-info']);
			}

			let response = data;

			if(isMobileApp) {
				response = getResult;
			}

			return response;
		} catch(error) {
			return handleError(error, options);
		}
	}

	static async post(url, data, options = {}) {
		try {
			const postOptions = {};

			let isMobileApp = false;

			if(options.isMobileApp && options.responseType) {
				isMobileApp = true;
				postOptions.baseURL = options.baseURL;
				postOptions.responseType = options.responseType;
				delete options.isMobileApp;
				delete options.responseType;
				delete options.baseURL;
			}

			const postResponse = await httpClient.post(url, getData(data), appendSelectionHeaders(postOptions));

			let response = postResponse.data;

			if(isMobileApp) {
				response = postResponse;
			}

			return response;
		} catch(error) {
			return handleError(error, options);
		}
	}

	static async put(url, data, options) {
		try {
			const response = await httpClient.put(url, getData(data), appendSelectionHeaders({}));

			return response.data;
		} catch(error) {
			return handleError(error, options);
		}
	}

	static async patch(url, data, options) {
		try {
			const response = await httpClient.patch(url, getData(data), appendSelectionHeaders({}));

			return response.data;
		} catch(error) {
			return handleError(error, options);
		}
	}

	static async delete(url, options) {
		try {
			const response = await httpClient.delete(url, appendSelectionHeaders(options));

			return response.data;
		} catch(error) {
			return handleError(error, options);
		}
	}
}

export default HttpRequester;

function getData(data) {
	if(data instanceof BaseModel) {
		return data.toJSON();
	} else if(_.isArray(data)) {
		return _.map(data, (item) => getData(item));
	} else if(_.isPlainObject(data)) {
		const keys = _.keys(data);

		for(const key of keys) {
			data[key] = getData(data[key]);
		}
	}

	return data;
}

function appendSelectionHeaders(options = {}) {
	options.headers = options.headers || {};

	if(!_.has(options.headers, 'dv-selected-company')) {
		options.headers['dv-selected-company'] = Store.state.FtgInfo && Store.state.FtgInfo.FtgId ? Store.state.FtgInfo.FtgId : '';
	}

	if(!_.has(options.headers, 'dv-selected-year')) {
		options.headers['dv-selected-year'] = Store.state.SalaryYear || '';
	}

	_.forEach(options.headers, (value, key) => {
		if(_.isNil(value)) {
			delete options.headers[key];
		}
	});

	return options;
}
